// Play-money odds for entertainment. Fair odds (no house edge), derived from the
// same Elo win chance as the tracker. Runs are two independent Poisson scores
// whose split is tuned so the model's win chance matches Elo.

const MAX_RUNS = 30;

function poisson(lambda) {
  const out = [];
  let p = Math.exp(-lambda);
  for (let k = 0; k <= MAX_RUNS; k += 1) {
    out.push(p);
    p = (p * lambda) / (k + 1);
  }
  return out;
}

function scoreGrid(lambdaHome, lambdaAway) {
  const H = poisson(lambdaHome);
  const A = poisson(lambdaAway);
  let home = 0;
  let away = 0;
  let tie = 0;
  for (let h = 0; h <= MAX_RUNS; h += 1) {
    for (let a = 0; a <= MAX_RUNS; a += 1) {
      const q = H[h] * A[a];
      if (h > a) home += q;
      else if (a > h) away += q;
      else tie += q;
    }
  }
  return { H, A, home, away, tie };
}

export const averageTotal = (games, fallback = 8.5) =>
  games.length
    ? Math.max(4, games.reduce((sum, game) => sum + game.homeScore + game.awayScore, 0) / games.length)
    : fallback;

export function marketsFor(homeWinChance, avgTotal) {
  let lo = 0.02;
  let hi = 0.98;
  for (let i = 0; i < 36; i += 1) {
    const split = (lo + hi) / 2;
    const grid = scoreGrid(avgTotal * split, avgTotal * (1 - split));
    // Ties go to extras, split by strength.
    const win = grid.home + (grid.tie * grid.home) / (grid.home + grid.away);
    if (win < homeWinChance) lo = split;
    else hi = split;
  }
  const split = (lo + hi) / 2;
  const { H, A } = scoreGrid(avgTotal * split, avgTotal * (1 - split));
  const totalLine = Math.max(1, Math.floor(avgTotal)) + 0.5;
  let homeBy2 = 0;
  let awayBy2 = 0;
  let over = 0;
  for (let h = 0; h <= MAX_RUNS; h += 1) {
    for (let a = 0; a <= MAX_RUNS; a += 1) {
      const q = H[h] * A[a];
      if (h - a >= 2) homeBy2 += q;
      if (a - h >= 2) awayBy2 += q;
      // A tie goes to extra innings, which adds at least one run.
      if (h + a > totalLine || (h === a && h + a === totalLine - 0.5)) over += q;
    }
  }
  return { homeWin: homeWinChance, homeBy2, awayBy2, totalLine, over };
}

/** American odds rounded to 5 like a sportsbook. -100 is shown as +100. */
export function american(p) {
  const c = Math.min(0.97, Math.max(0.03, p));
  const raw = c >= 0.5 ? (-100 * c) / (1 - c) : (100 * (1 - c)) / c;
  const a = Math.round(raw / 5) * 5;
  return a === -100 ? 100 : a;
}

export const decimalOdds = (a) => (a > 0 ? 1 + a / 100 : 1 + 100 / Math.abs(a));
export const formatAmerican = (a) => (a > 0 ? `+${a}` : `−${Math.abs(a)}`);
export const formatOdds = (a, format) => (format === 'decimal' ? decimalOdds(a).toFixed(2) : formatAmerican(a));

/** Total returned (amount + profit) for a play-money amount. */
export const payout = (amount, a) => amount * decimalOdds(a);

/** Board rows, away on the left and home on the right (matches the scoreboard). */
export function boardRows(markets, homeName, awayName) {
  const homeFav = markets.homeWin >= 0.5;
  return [
    {
      market: 'Moneyline',
      cells: [
        { id: 'ml-away', label: awayName, chance: 1 - markets.homeWin, description: `${awayName} to win` },
        { id: 'ml-home', label: homeName, chance: markets.homeWin, description: `${homeName} to win` },
      ],
    },
    {
      market: 'Run line',
      cells: homeFav
        ? [
            { id: 'rl-away', label: awayName, line: '+1.5', chance: 1 - markets.homeBy2, description: `${awayName} to win or lose by 1` },
            { id: 'rl-home', label: homeName, line: '−1.5', chance: markets.homeBy2, description: `${homeName} to win by 2+` },
          ]
        : [
            { id: 'rl-away', label: awayName, line: '−1.5', chance: markets.awayBy2, description: `${awayName} to win by 2+` },
            { id: 'rl-home', label: homeName, line: '+1.5', chance: 1 - markets.awayBy2, description: `${homeName} to win or lose by 1` },
          ],
    },
    {
      market: 'Total runs',
      cells: [
        { id: 'over', label: 'Over', line: String(markets.totalLine), chance: markets.over, description: `Over ${markets.totalLine} total runs` },
        { id: 'under', label: 'Under', line: String(markets.totalLine), chance: 1 - markets.over, description: `Under ${markets.totalLine} total runs` },
      ],
    },
  ];
}

/** Winner's pre-game American price if they were the underdog (+105 or longer), else null. */
export function upsetPrice(homeWinChance, homeWon) {
  const a = american(homeWon ? homeWinChance : 1 - homeWinChance);
  return a > 100 ? a : null;
}
